import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./HomePage.css";
import BottomNav from "./BottomNav";
import {
  FaHome,
  FaGamepad,
  FaHeadphonesAlt,
  FaTrophy,
  FaTrailer,
} from "react-icons/fa";

// Helper functions
const formatProfit = (num) =>
  `$${num.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const getRandomAccount = () => {
  const letters = "abcdefghijklmnopqrstuvwxyz";
  let acc = "";
  for (let i = 0; i < 6; i++) acc += letters[Math.floor(Math.random() * letters.length)];
  return acc.slice(0, 3) + "***" + acc.slice(3);
};


// Generate leaderboard (descending order)
const generateLeaders = () => {
  const accounts = [
    "Alexander Thomas ",
    "Oliver Maxim",
    "Lucas William",
    "Leonardo Curse ",
    "Noah james",
    "Arnold Smith",
    "Matteo Rendrick",
    "Benjamin Diaz",
    "daniel Carlos",
    "Davis Rob",
    "Andrew Davidson",
    "Rajkhanna",
  ];

  const leaders = accounts.slice(0, 10).map((name, index) => {
    const baseProfit = 300000 + index * 5000; // base incremental
    const variation = Math.floor(Math.random() * 10000 + 10000); // 10k–20k random
    const profit = baseProfit + variation;
    return {
      rank: index + 1,
      account: name,
      profit,
    };
  });

  const sorted = leaders.sort((a, b) => b.profit - a.profit);
  return sorted.map((l, i) => ({ ...l, rank: i + 1 }));
};


// Generate reviews (English + online avatars)
const generateReviews = () => {
  const reviewers = [
    "acacia kersey",
    "nanakoot_",
    "jennifercoppe_",
    "tasyafarasy",
    "eronaaademii",
    "jonazgirl",
    "fatlxma",
    "meganxharjaa27",
    "Narueemon",
    "Davis Rob",
    "aaronkc99",
    "meowinthedisco",
  ];

  const sampleTexts = [
    "Great experience! My withdrawal was smooth and fast 🔥",
    "This platform really pays, I’m so happy 😍",
    "The profit keeps coming in daily, amazing!",
    "I’ve tried similar apps, but this one is truly the best 💯",
    "Customer service is super friendly and helpful 🙏",
    "So easy to use and the bonuses are real 💰",
    "I made my first profit in just one day! 😎",
    "I didn’t expect it to work, but it really does! 🎉",
    "Everything is transparent and smooth so far 👏",
    "Highly recommended — 100% legit and fast payout 🚀",
  ];

  return reviewers.slice(0, 10).map((name, i) => ({
    id: i + 1,
    author: name,
    text: sampleTexts[i % sampleTexts.length],
    avatar: `https://randomuser.me/api/portraits/${Math.random() > 0.5 ? "men" : "women"
      }/${Math.floor(Math.random() * 80)}.jpg`,
  }));
};

// ✅ Fixed: curated real Unsplash images with luxury/success theme
const generateLuxuryPosts = () => {
  const posts = [
    {
      caption: "Just won my dream car today! 🚗💨",
      img: "https://images.unsplash.com/photo-1502877338535-766e1452684a?w=1200&q=80",
    },
    {
      caption: "Can’t believe I received this big reward 💸🔥",
      img: "https://assets.promediateknologi.id/crop/0x0:0x0/750x500/webp/photo/2022/04/07/1239340314.jpg",
    },
    {
      caption: "Another successful day, life keeps getting better!",
      img: "https://images.unsplash.com/photo-1556742031-c6961e8560b0?w=1200&q=80",
    },
    {
      caption: "Luxury and success — my new lifestyle 😎",
      img: "https://asset.kompas.com/crops/yhD91XCkBWobIHEzUqNjgVSTo1U=/0x22:642x450/1200x800/data/photo/2021/09/26/61504c5913781.png",
    },
    {
      caption: "Finally achieved my dream, thank you 🙏✨",
      img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1200&q=80",
    },
    {
      caption: "Big jackpot win! Feeling blessed 💰🎉",
      img: "https://imgcdn.espos.id/@espos/images/2022/08/ilustrasi-mendapatkan-uang.jpg?quality=60",
    },
    {
      caption: "From zero to luxury, dreams do come true 🏆",
      img: "https://wl-brightside.cf.tsp.li/resize/728x/jpg/041/9d6/7d757351b3a42e4d4b339907b2.jpg",
    },
    {
      caption: "I still can’t believe this car is mine 😍",
      img: "https://shopee.co.id/inspirasi-shopee/wp-content/uploads/2023/08/Ferrari-Raffi-Ahmad.webp",
    },
    {
      caption: "Winning feels amazing — hard work pays off!",
      img: "https://media.istockphoto.com/id/840497622/photo/a-man-holds-dollars-in-his-hands.jpg?s=612x612&w=0&k=20&c=RdrJJC7jdXkw1IUhIeDPehL4YCLkBsEUsdei2gINFnA=",
    },
    {
      caption: "What a day! Success tastes sweet 🎊",
      img: "https://assets.promediateknologi.id/crop/110x141:605x362/750x500/webp/photo/2023/02/22/Shio-diberkahi-dewa-cai-sen-268717519.jpg",
    },
  ];
  return posts.map((p, i) => ({ id: i + 1, ...p }));
};

export default function HomePage() {
  const [leaders, setLeaders] = useState(generateLeaders());
  const [reviews, setReviews] = useState(generateReviews());
  const [posts, setPosts] = useState(generateLuxuryPosts());
  const [currentReview, setCurrentReview] = useState(0);
  const navigate = useNavigate();

  // Auto-rotate reviews
  useEffect(() => {
    const t = setInterval(() => {
      setCurrentReview((c) => (c + 1) % reviews.length);
    }, 3500);
    return () => clearInterval(t);
  }, [reviews]);

  // Current date
  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB");

  // Split posts into 2 columns
  const columns = useMemo(() => {
    const left = [], right = [];
    posts.forEach((p, i) => (i % 2 === 0 ? left.push(p) : right.push(p)));
    return { left, right };
  }, [posts]);

  return (
    <div className="tb-homepage">
      <main className="tb-main">
        {/* Top Menu same as HomePage */}
        <div className="d-flex justify-content-around text-center mb-4 menu-row">
          <div className="menu-btn" onClick={() => navigate("/")}>
            <FaHome className="menu-icon" />
            <span>Lobby</span>
          </div>
          <div className="menu-btn" onClick={() => navigate("/game")}>
            <FaGamepad className="menu-icon" />
            <span>Game</span>
          </div>
          <div className="menu-btn active" onClick={() => navigate("/esport")}>
            <FaTrophy className="menu-icon" />
            <span>eSports</span>
          </div>
          <div className="menu-btn active" onClick={() => navigate("/music")}>
            <FaHeadphonesAlt className="menu-icon" />
            <span>Music</span>
          </div>
          <div className="menu-btn" onClick={() => navigate("/FullPage")}>
            <FaTrailer className="menu-icon" />
            <span>All</span>
          </div>
        </div>

        {/* Leaderboard */}
        <section className="tb-leaderboard-card">
          <div className="leader-title">Leaderboard</div>
          <table className="leader-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Account</th>
                <th>Profit</th>
              </tr>
            </thead>
            <tbody>
              {leaders.map((l, i) => (
                <tr key={i}>
                  <td>{l.rank}</td>
                  <td>{l.account}</td>
                  <td>{formatProfit(l.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="leader-footer">Last updated on {formattedDate}</div>
          <div className="leader-medal" aria-hidden>
            🏅
          </div>
        </section>

        {/* CTA */}
        <section className="tb-cta">
          <h3>Like &amp; Watch</h3>
          <p>Earn profit when you like and watch videos</p>
          <button
            className="cta-btn"
            onClick={() => navigate("/packageinfo")} // ✅ redirect ke PackageInfo.js
          >
            View Now
          </button>
        </section>


        {/* Reviews */}
        <section className="tb-reviews">
          <div className="review-card">
            <div className="review-avatar">
              <img src={reviews[currentReview].avatar} alt="avatar" />
            </div>
            <div className="review-content">
              <div className="review-author">{reviews[currentReview].author}</div>
              <div className="review-text">{reviews[currentReview].text}</div>
            </div>
          </div>

          <div className="review-dots">
            {reviews.map((r, i) => (
              <button
                key={r.id}
                className={`dot ${i === currentReview ? "active" : ""}`}
                onClick={() => setCurrentReview(i)}
                aria-label={`review ${i + 1}`}
              />
            ))}
          </div>
        </section>

        {/* Posts */}
        {/*<section className="tb-posts">
          <div className="col left">
            {columns.left.map((p) => (
              <article className="post-card" key={p.id}>
                <img src={p.img} alt="post" className="post-img" />
                <div className="post-caption">{p.caption}</div>
              </article>
            ))}
          </div>
          <div className="col right">
            {columns.right.map((p) => (
              <article className="post-card" key={p.id}>
                <img src={p.img} alt="post" className="post-img" />
                <div className="post-caption">{p.caption}</div>
              </article>
            ))}
          </div>
        </section>*/}
      </main>

      <BottomNav />
    </div>
  );
}
